/**
 * Cross-Selling-Galerien (Stufe D): Liste, Detail mit Einstellungen und Regeln, Match-Vorschau
 * „welche Angebote zeigt die Galerie zu diesem Angebot?“ – auch für einen ungespeicherten
 * Regelentwurf. Bearbeiten und Neuanlage in `gallery-editor.tsx`; Einbettcode folgt mit dem Erzeuger.
 */
import { Alert, Anchor, Badge, Button, Card, Code, ColorSwatch, CopyButton, Group, Image, Loader, Modal, Select, SimpleGrid, Stack, Table, Text, Title, type AnchorProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createLink, useNavigate } from '@tanstack/react-router';
import { forwardRef, useEffect, useState, type AnchorHTMLAttributes } from 'react';
import type { GalleryDetailResponse, GallerySummaryView, GalleryUsageResponse, MatchResponse, RulesDraft } from '../../../shared/galleries.js';
import { ApiRequestError } from '../api/client.js';
import { CreateGalleryForm, draftOf, GallerySettingsForm, RulesEditor, rowOf, type RuleRow } from './gallery-editor.js';
import { siteLabel } from '../sites.js';
import { has, money, t, type TextKey } from '../texts.js';
import { useFrozen, useServices } from '../services.js';
import { FullPageLoader, QueryErrorState } from './tenant-layout.js';
import { BackLink } from './back-link.js';

/** Mantine-Anchor als Router-Link. */
const RouterAnchor = createLink(
  forwardRef<HTMLAnchorElement, AnchorProps & AnchorHTMLAttributes<HTMLAnchorElement>>(function LinkAnchor(props, ref) {
    return <Anchor component="a" ref={ref} {...props} />;
  }),
);

const SORT_TEXT: Record<number, TextKey> = {
  0: 'galleries.sort.none',
  1: 'galleries.sort.newest',
  2: 'galleries.sort.priceAsc',
  3: 'galleries.sort.priceDesc',
  4: 'galleries.sort.none',
  5: 'galleries.sort.none',
};

function label(prefix: string, value: string): string {
  const key = `${prefix}.${value}`;
  return has(key) ? t(key) : value;
}

function LinkModeBadge({ gallery }: { gallery: GallerySummaryView }) {
  const text = gallery.linkMode === 'custom' ? t('galleries.link.customCount', { n: gallery.ruleCount + gallery.scriptCount }) : label('galleries.link', gallery.linkMode);
  return <Badge variant="light">{text}</Badge>;
}

export function GalleriesPage({ tenantId }: { tenantId: string }) {
  const { api } = useServices();
  const frozen = useFrozen();
  const [creating, setCreating] = useState(false);
  const query = useQuery({ queryKey: ['galleries', tenantId], queryFn: () => api.galleries(tenantId) });
  if (query.isPending) return <FullPageLoader />;
  if (query.isError) return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const { galleries } = query.data;
  // Galerie-Regeleditor eingefroren (P8): Liste und Detail nur lesend.
  const editable = query.data.editable && !frozen;
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>{t('galleries.title')}</Title>
        {editable && !creating && <Button onClick={() => setCreating(true)}>{t('galleries.create.open')}</Button>}
      </Group>
      {creating && <CreateGalleryForm tenantId={tenantId} onCancel={() => setCreating(false)} />}
      {galleries.length === 0 ? (
        <Text c="dimmed">{t('galleries.empty')}</Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('galleries.col.name')}</Table.Th>
              <Table.Th>{t('galleries.col.g')}</Table.Th>
              <Table.Th>{t('items.site')}</Table.Th>
              <Table.Th>{t('galleries.col.link')}</Table.Th>
              <Table.Th>{t('galleries.col.state')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {galleries.map(gallery => (
              <Table.Tr key={gallery.galleryId}>
                <Table.Td>
                  <RouterAnchor to="/t/$tenantId/galleries/$galleryId" params={{ tenantId, galleryId: gallery.galleryId }}>
                    {gallery.name}
                  </RouterAnchor>
                </Table.Td>
                <Table.Td>{gallery.legacyG}</Table.Td>
                <Table.Td>{siteLabel(gallery.site)}</Table.Td>
                <Table.Td>
                  <LinkModeBadge gallery={gallery} />
                </Table.Td>
                <Table.Td>{gallery.locked ? <Badge color="yellow">{t('galleries.locked')}</Badge> : t('common.unknown')}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function Fact({ name, value }: { name: string; value: string }) {
  return (
    <Group justify="space-between" gap="xs" wrap="nowrap">
      <Text size="sm" c="dimmed">
        {name}
      </Text>
      <Text size="sm" ta="right">
        {value}
      </Text>
    </Group>
  );
}

const yesNo = (value: boolean) => (value ? t('common.yes') : t('common.no'));

const COLOR_KEYS = ['c1', 'c3', 'i1', 'i2', 'i3'] as const;

/** Farben des Einbettcodes (sgallery c1 Hintergrund, c3 Rahmen, i1..i3 Angebot; c2 Schrift wird abgeleitet). */
function Colors({ colors }: { colors: GalleryDetailResponse['gallery']['settings']['colors'] }) {
  if (!colors) return <Fact name={t('galleries.colors')} value={t('galleries.colors.default')} />;
  return (
    <Group justify="space-between" gap="xs" wrap="nowrap">
      <Text size="sm" c="dimmed">
        {t('galleries.colors')}
      </Text>
      <Group gap={4}>
        {COLOR_KEYS.map(key => (
          <ColorSwatch key={key} color={`#${colors[key]}`} size={16} title={`${t(`galleries.colors.${key}`)}: #${colors[key]}`} aria-label={`${t(`galleries.colors.${key}`)}: #${colors[key]}`} />
        ))}
      </Group>
    </Group>
  );
}

/**
 * Vorschau und HTML-Code der Galerie ohne Regeln: beliebige aktive Angebote des Kontos wie im Altsystem ohne Ausgangsangebot
 * (Flori 01.11.2026); liest nur eine Indexseite, deshalb beim Öffnen geladen.
 */
function GalleryPreview({ tenantId, galleryId }: { tenantId: string; galleryId: string }) {
  const { api } = useServices();
  const preview = useQuery({ queryKey: ['gallery-preview', tenantId, galleryId], queryFn: () => api.galleryPreview(tenantId, galleryId) });
  return (
    <Card withBorder padding="lg">
      <Stack gap="sm">
        <Title order={4}>{t('galleries.preview.title')}</Title>
        <Text size="sm" c="dimmed">
          {t('galleries.preview.hint')}
        </Text>
        {preview.isPending ? (
          <Loader size="sm" />
        ) : preview.isError ? (
          <Alert color="red" role="alert">
            {(preview.error as { detail?: string }).detail ?? t('common.error')}
          </Alert>
        ) : preview.data.items.length === 0 ? (
          <Text c="dimmed">{t('galleries.preview.none')}</Text>
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }}>
            {preview.data.items.map(item => (
              <Card key={item.ebayItemId} withBorder padding="xs">
                {item.pictureUrl && <Image src={item.pictureUrl} alt="" h={80} fit="contain" referrerPolicy="no-referrer" />}
                <Text size="xs" lineClamp={2}>
                  {item.title}
                </Text>
                <Text size="xs" fw={600}>
                  {money(item.price.amount, item.price.currency)}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
        )}
        {preview.data?.embed && (
          <Stack gap={4}>
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                {t('galleries.embed.title')}
              </Text>
              <CopyButton value={preview.data.embed.html}>
                {({ copied, copy }) => (
                  <Button size="xs" variant="light" onClick={copy}>
                    {copied ? t('galleries.embed.copied') : t('galleries.embed.copy')}
                  </Button>
                )}
              </CopyButton>
            </Group>
            <Code block aria-label={t('galleries.embed.title')} style={{ maxHeight: 220, overflow: 'auto' }}>
              {preview.data.embed.html}
            </Code>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

/** `draft`: `undefined` = gespeicherte Regeln, `null` = Entwurf noch fehlerhaft (keine Vorschau möglich). */
function MatchPreview({
  tenantId,
  galleryId,
  site,
  rulesVersion,
  draft,
}: {
  tenantId: string;
  galleryId: string;
  site: number;
  rulesVersion: number;
  draft?: RulesDraft | null;
}) {
  const { api } = useServices();
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const [sourceItemId, setSourceItemId] = useState<string | null>(null);
  // Ausgangsangebote aller Sites (S7): ein Angebot einer anderen Site zeigt die Galerie mit Zielangeboten der Galeriesite.
  const candidates = useQuery({
    queryKey: ['items', tenantId, { q: debounced, purpose: 'match-source' }],
    queryFn: () => api.items(tenantId, { status: 'active', limit: 20, ...(debounced.trim() ? { q: debounced.trim() } : {}) }),
  });
  const match = useMutation({ mutationFn: (source: string | null) => api.match(tenantId, galleryId, source ?? undefined, draft ?? undefined) });
  // Ein Ergebnis gilt nur für den Stand, mit dem es berechnet wurde: Entwurf geändert, gespeichert
  // oder verworfen → altes Ergebnis ausblenden.
  const basis = `${rulesVersion}:${draft === undefined ? 'gespeichert' : JSON.stringify(draft)}`;
  const { reset } = match;
  useEffect(() => reset(), [basis, reset]);
  const result: MatchResponse | undefined = match.data;
  return (
    <Card withBorder padding="lg">
      <Stack gap="sm">
        <Title order={4}>{t('galleries.match.title')}</Title>
        <Text size="sm" c="dimmed">
          {t('galleries.match.hint')}
        </Text>
        <Group align="end">
          <Select
            label={t('galleries.match.source')}
            placeholder={t('items.search')}
            searchable
            clearable
            w={420}
            searchValue={search}
            onSearchChange={setSearch}
            value={sourceItemId}
            onChange={setSourceItemId}
            filter={({ options }) => options}
            nothingFoundMessage={t('items.empty')}
            data={(candidates.data?.items ?? []).map(item => ({
              value: item.ebayItemId,
              label: `${item.title} (${item.ebayItemId}${item.site === site ? '' : `, ${siteLabel(item.site)}`})`,
            }))}
          />
          <Button loading={match.isPending} disabled={draft === null} onClick={() => match.mutate(sourceItemId)}>
            {draft === undefined ? t('galleries.match.run') : t('galleries.match.runDraft')}
          </Button>
        </Group>
        {draft === null && (
          <Text size="xs" c="dimmed">
            {t('galleries.match.draftInvalid')}
          </Text>
        )}
        {match.isError && (
          // 409: fachlicher Grund der Regeln (gesperrt, nicht übersetzbar), kein Störfall – gelb wie der Sperrhinweis.
          <Alert color={match.error instanceof ApiRequestError && match.error.status === 409 ? 'yellow' : 'red'} role="alert">
            {(match.error as { detail?: string }).detail ?? t('common.error')}
          </Alert>
        )}
        {result && (
          <Stack gap="xs">
            <Group gap="xs">
              {result.context.draftHash && <Badge color="grape">{t('galleries.match.draft')}</Badge>}
              <Badge color={result.selectionComplete ? 'green' : 'yellow'}>
                {t(result.selectionComplete ? 'galleries.match.selection.complete' : 'galleries.match.selection.open')}
              </Badge>
              <Badge color={result.completeness === 'complete' ? 'green' : result.completeness === 'partial' ? 'blue' : 'yellow'}>
                {label('galleries.match.completeness', result.completeness)}
              </Badge>
              {result.usedFallback && <Badge color="orange">{t('galleries.match.fallback')}</Badge>}
              {result.missingData.map(missing => (
                <Badge key={missing} color="gray" variant="outline">
                  {t('galleries.match.missing')}: {label('galleries.match.missingData', missing)}
                </Badge>
              ))}
            </Group>
            {result.appliedRules.length > 0 && (
              <Text size="sm">
                {result.appliedRules.map(rule => `${label('rules.kind', rule.kind)}: ${t('galleries.match.hits', { n: rule.hits })}`).join(' · ')}
              </Text>
            )}
            {result.items.length === 0 ? (
              <Text c="dimmed">{t('galleries.match.none')}</Text>
            ) : (
              <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }}>
                {result.items.map(item => (
                  <Card key={item.ebayItemId} withBorder padding="xs">
                    {item.pictureUrl && <Image src={item.pictureUrl} alt="" h={80} fit="contain" referrerPolicy="no-referrer" />}
                    <Text size="xs" lineClamp={2}>
                      {item.title}
                    </Text>
                    <Text size="xs" fw={600}>
                      {money(item.price.amount, item.price.currency)}
                    </Text>
                  </Card>
                ))}
              </SimpleGrid>
            )}
            <Text size="xs" c="dimmed">
              {t('galleries.match.context', { version: result.context.rulesVersion, max: result.context.maxItems })}
            </Text>
            {result.context.sourceSite !== undefined && (
              <Alert color="blue" role="note">
                {t('galleries.match.crossSite', { source: siteLabel(result.context.sourceSite), target: siteLabel(result.context.site) })}
              </Alert>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function reasonKey(state: GalleryUsageResponse['removal']): TextKey {
  const key = `galleries.delete.reason.${state}`;
  return has(key) ? key : 'galleries.delete.reason.other';
}

/** Was der Hinweis zum Löschen über die Beschreibungen sagt (ehrlich je Zustand, ohne Versprechen ohne Mechanismus). */
function deleteNotes(usage: GalleryUsageResponse): string[] {
  const notes = [
    usage.removal === 'requested'
      ? t('galleries.delete.removes')
      : t('galleries.delete.keeps', { reason: t(reasonKey(usage.removal)) }),
    t('galleries.delete.fullMode'),
  ];
  if (usage.insertMode !== 'none') notes.push(t('galleries.delete.insert', { position: t(`galleries.delete.insert.${usage.insertMode}`) }));
  if (usage.designs > 0) notes.push(t('galleries.delete.designs', { designs: usage.designs, articles: usage.articles }));
  // Einbettungen laut letztem Schreibstand; Teilzählung, Ausfall und Unbekanntes nie als Gesamtzahl oder als „keine“.
  const embeds = usage.embeds;
  if (!embeds) notes.push(t('galleries.delete.embedsUnknown'));
  else if (!embeds.complete) notes.push(t('galleries.delete.embedsPartial', { n: embeds.count }));
  else if (embeds.count > 0) notes.push(t('galleries.delete.embeds', { n: embeds.count }));
  if (embeds && embeds.unresolved > 0) notes.push(t(embeds.complete ? 'galleries.delete.embedsUnresolved' : 'galleries.delete.embedsUnresolvedPartial', { n: embeds.unresolved }));
  return notes;
}

/** Weich löschen (Flori 25.10.2026): erlaubt, auch wenn verwendet; vorher zeigt der Dialog, was das bewirkt. */
function DeleteGalleryButton({ tenantId, gallery }: { tenantId: string; gallery: GalleryDetailResponse['gallery'] }) {
  const { api } = useServices();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const usage = useQuery({ queryKey: ['gallery-usage', tenantId, gallery.galleryId], queryFn: () => api.galleryUsage(tenantId, gallery.galleryId), enabled: open });
  const done = () => {
    setOpen(false);
    queryClient.removeQueries({ queryKey: ['gallery', tenantId, gallery.galleryId] });
    void queryClient.invalidateQueries({ queryKey: ['galleries', tenantId] });
    void navigate({ to: '/t/$tenantId/galleries', params: { tenantId } });
  };
  const remove = useMutation({
    mutationFn: () => api.deleteGallery(tenantId, gallery.galleryId, gallery.version),
    onSuccess: done,
    // Antwort verloren und erneut gesendet: die Galerie ist schon gelöscht (404) – wie Erfolg behandeln.
    onError: error => {
      if (error instanceof ApiRequestError && error.status === 404) done();
      else {
        void queryClient.invalidateQueries({ queryKey: ['gallery', tenantId, gallery.galleryId] });
        void queryClient.invalidateQueries({ queryKey: ['gallery-usage', tenantId, gallery.galleryId] });
      }
    },
  });
  // Läuft die Löschung schon, bleibt der Dialog offen (sonst navigierte die Antwort aus einem geschlossenen Dialog).
  const close = () => {
    if (remove.isPending) return;
    setOpen(false);
    remove.reset();
  };
  return (
    <>
      <Button size="xs" variant="subtle" color="red" onClick={() => setOpen(true)}>
        {t('galleries.delete')}
      </Button>
      <Modal opened={open} onClose={close} title={t('galleries.delete.title')} closeOnClickOutside={!remove.isPending} closeOnEscape={!remove.isPending} withCloseButton={!remove.isPending}>
        <Stack gap="sm">
          <Text size="sm">{t('galleries.delete.text', { name: gallery.name })}</Text>
          {usage.isPending ? (
            <Loader size="sm" />
          ) : usage.isError ? (
            <Text size="sm" c="dimmed">
              {t('galleries.delete.usageFailed')}
            </Text>
          ) : (
            <Alert color="yellow" variant="light">
              <Stack gap={4}>
                {deleteNotes(usage.data).map(note => (
                  <Text key={note} size="sm">
                    {note}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}
          {remove.isError && !(remove.error instanceof ApiRequestError && remove.error.status === 404) && (
            <Alert color="red" role="alert">
              {remove.error instanceof ApiRequestError && remove.error.detail ? remove.error.detail : t('common.saveFailed')}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={close} disabled={remove.isPending}>
              {t('common.cancel')}
            </Button>
            <Button color="red" onClick={() => remove.mutate()} loading={remove.isPending} disabled={usage.isPending || usage.isFetching}>
              {t('galleries.delete.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/** Angebote mit dieser Galerie (Ist-Stand `EMBED#`); geladen erst auf Wunsch, weil ohne Index seitenweise gelesen wird. */
function GalleryItems({ tenantId, galleryId }: { tenantId: string; galleryId: string }) {
  const { api } = useServices();
  const [shown, setShown] = useState(false);
  const query = useInfiniteQuery({
    queryKey: ['gallery-items', tenantId, galleryId],
    queryFn: ({ pageParam }) => api.galleryItems(tenantId, galleryId, pageParam ? { cursor: pageParam } : {}),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: last => last.nextCursor,
    enabled: shown,
  });
  const entries = query.data?.pages.flatMap(p => p.entries) ?? [];
  return (
    <Card withBorder padding="lg">
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={4}>{t('galleries.items.title')}</Title>
          {!shown && (
            <Button size="xs" variant="light" onClick={() => setShown(true)}>
              {t('galleries.items.show')}
            </Button>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          {t('galleries.items.intro')}
        </Text>
        {shown &&
          (query.isPending ? (
            <Loader size="sm" />
          ) : query.isError && !query.isFetchNextPageError ? (
            <Alert color="red" role="alert">
              <Group justify="space-between">
                <Text size="sm">{query.error instanceof ApiRequestError && query.error.detail ? query.error.detail : t('common.error')}</Text>
                <Button size="xs" variant="light" onClick={() => void query.refetch()}>
                  {t('common.retry')}
                </Button>
              </Group>
            </Alert>
          ) : entries.length === 0 && !query.hasNextPage ? (
            <Text size="sm">{t('galleries.items.none')}</Text>
          ) : (
            <>
              {entries.length > 0 && (
                <Table>
                  <Table.Tbody>
                    {entries.map(entry => (
                      <Table.Tr key={entry.ebayItemId}>
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>
                            {entry.item?.title ?? t('galleries.items.missing')}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {entry.ebayItemId}
                            {entry.item && entry.item.status !== 'active' ? ` · ${t(`items.state.${entry.item.status}`)}` : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td>{entry.mode ? t(`galleries.items.mode.${entry.mode}`) : ''}</Table.Td>
                        <Table.Td>{entry.item?.embedState ? <Badge size="xs" variant="outline">{t(`embed.${entry.item.embedState}`)}</Badge> : null}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              {query.isFetchNextPageError && (
                <Alert color="red" role="alert">
                  {query.error instanceof ApiRequestError && query.error.detail ? query.error.detail : t('common.error')}
                </Alert>
              )}
              {query.hasNextPage && (
                <Group gap="xs">
                  {/* Ohne Index nach Galerie liest jede Seite nur ein Stück des Bestands; leere Seiten heißen „noch nicht fertig“. */}
                  {query.data?.pages.at(-1)?.entries.length === 0 && (
                    <Text size="xs" c="dimmed">
                      {t('galleries.items.searching')}
                    </Text>
                  )}
                  <Button size="xs" variant="default" onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>
                    {query.data?.pages.at(-1)?.entries.length === 0 ? t('galleries.items.searchOn') : t('galleries.items.more')}
                  </Button>
                </Group>
              )}
            </>
          ))}
      </Stack>
    </Card>
  );
}

export function GalleryPage({ tenantId, galleryId }: { tenantId: string; galleryId: string }) {
  const { api } = useServices();
  const frozen = useFrozen();
  const query = useQuery({ queryKey: ['gallery', tenantId, galleryId], queryFn: () => api.gallery(tenantId, galleryId) });
  const [editingSettings, setEditingSettings] = useState(false);
  // Regelentwurf lebt hier, damit die Vorschau ihn ohne Speichern auswerten kann.
  const [ruleRows, setRuleRows] = useState<RuleRow[] | undefined>(undefined);
  const [ruleBase, setRuleBase] = useState(0);
  const [removeScripts, setRemoveScripts] = useState(false);
  if (query.isPending) return <FullPageLoader />;
  if (query.isError) return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const detail = query.data;
  const { gallery } = detail;
  // Galerie-Regeleditor eingefroren (P8): nur lesend, keine Match-Vorschau.
  const rules = { ...detail.rules, editable: detail.rules.editable && !frozen };
  const settings = gallery.settings;
  const draft = ruleRows === undefined ? undefined : (draftOf(ruleRows, removeScripts) ?? null);
  const stopRules = () => {
    setRuleRows(undefined);
    setRemoveScripts(false);
  };
  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <BackLink to="/t/$tenantId/galleries" params={{ tenantId }} />
        <Group justify="space-between">
          <Title order={2}>{gallery.name}</Title>
          {rules.editable && <DeleteGalleryButton tenantId={tenantId} gallery={gallery} />}
        </Group>
      </Stack>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Card withBorder padding="lg">
          <Stack gap="xs">
            <Group justify="space-between">
              <Title order={4}>{t('galleries.settings')}</Title>
              {rules.editable && !editingSettings && (
                <Button size="xs" variant="light" onClick={() => setEditingSettings(true)}>
                  {t('galleries.edit.settings')}
                </Button>
              )}
            </Group>
            {editingSettings ? (
              <GallerySettingsForm tenantId={tenantId} detail={detail} onDone={() => setEditingSettings(false)} />
            ) : (
              <>
            <Fact name={t('galleries.col.g')} value={gallery.legacyG} />
            <Fact name={t('items.site')} value={siteLabel(gallery.site)} />
            <Fact name={t('galleries.maxItems')} value={String(settings.maxItems)} />
            <Fact name={t('galleries.height')} value={String(settings.height ?? 2)} />
            <Fact name={t('galleries.width')} value={settings.width ? `${settings.width} px` : t('galleries.width.auto')} />
            <Fact name={t('galleries.bigPicture')} value={yesNo(settings.bigPicture ?? false)} />
            <Fact name={t('galleries.sameSiteOnly')} value={yesNo(settings.sameSiteOnly ?? true)} />
            <Fact name={t('galleries.sort')} value={`${t(SORT_TEXT[settings.sortMode] ?? 'galleries.sort.none')}${settings.randomize ? `, ${t('galleries.random')}` : ''}`} />
            <Fact name={t('galleries.titleLines')} value={settings.titleLines === 0 ? t('common.no') : String(settings.titleLines)} />
            <Fact name={t('galleries.showPrice')} value={yesNo(settings.showPrice)} />
            <Fact name={t('galleries.showAuctionType')} value={yesNo(settings.showAuctionType)} />
            <Fact name={t('galleries.showInfoText')} value={yesNo(settings.showInfoText)} />
            <Fact name={t('galleries.language')} value={settings.language.toUpperCase()} />
            <Colors colors={settings.colors} />
            {!rules.editable && (
              <Text size="xs" c="dimmed">
                {t('galleries.readOnlyHint')}
              </Text>
            )}
              </>
            )}
          </Stack>
        </Card>
        <Card withBorder padding="lg">
          <Stack gap="xs">
            <Group justify="space-between">
              <Title order={4}>{t('galleries.rules')}</Title>
              <Group gap="xs">
                <LinkModeBadge gallery={gallery} />
                {rules.editable && ruleRows === undefined && (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => {
                      setRuleBase(rules.version);
                      setRuleRows(rules.ast.map(rowOf));
                    }}
                  >
                    {t('galleries.edit.rules')}
                  </Button>
                )}
              </Group>
            </Group>
            {rules.locked && (
              <Alert color="yellow" title={t('galleries.locked')}>
                {rules.locked.reason}
              </Alert>
            )}
            {rules.scripts.map(script => (
              <Stack key={script.scriptId} gap={2}>
                <Text size="sm" fw={600}>
                  {t('galleries.script')}: {script.name ?? script.scriptId} ({t('galleries.scriptLines', { n: script.lineCount })}
                  {script.unsupportedLineCount > 0 ? `, ${t('galleries.unsupported', { n: script.unsupportedLineCount })}` : ''})
                </Text>
                {script.lines.map(line => (
                  <Text key={line.lineNo} size="xs" ff="monospace">
                    {line.lineNo}: {line.text}
                  </Text>
                ))}
              </Stack>
            ))}
            {rules.rules.length > 0 && (
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('galleries.priority')}</Table.Th>
                    <Table.Th>{t('galleries.ruleKind')}</Table.Th>
                    <Table.Th>{t('galleries.ruleDetail')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rules.rules.map(rule => (
                    <Table.Tr key={rule.ruleId}>
                      <Table.Td>{rule.priority}</Table.Td>
                      <Table.Td>{label('rules.kind', rule.kind)}</Table.Td>
                      <Table.Td>{rule.summary || t('common.unknown')}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {rules.rules.length === 0 && rules.scripts.length === 0 && <Text size="sm">{t('galleries.link.all')}</Text>}
          </Stack>
        </Card>
      </SimpleGrid>
      {ruleRows !== undefined && (
        <Card withBorder padding="lg">
          <Stack gap="sm">
            <Title order={4}>{t('galleries.rules.editTitle')}</Title>
            <Text size="sm" c="dimmed">
              {t('galleries.rules.editHint')}
            </Text>
            <RulesEditor
              tenantId={tenantId}
              detail={detail}
              baseVersion={ruleBase}
              rows={ruleRows}
              setRows={setRuleRows}
              removeScripts={removeScripts}
              setRemoveScripts={setRemoveScripts}
              onDone={stopRules}
            />
          </Stack>
        </Card>
      )}
      {/* Nur lesen und günstig: auch im eingefrorenen Bereich (Regeleditor und Regeltest bleiben eingefroren). */}
      <GalleryPreview tenantId={tenantId} galleryId={gallery.galleryId} />
      {/* Regeltest nur beim Bearbeiten der Regeln; die Vorschau oben beachtet keine Regeln. */}
      {!frozen && ruleRows !== undefined && <MatchPreview tenantId={tenantId} galleryId={gallery.galleryId} site={gallery.site} rulesVersion={rules.version} {...(draft !== undefined ? { draft } : {})} />}
      <GalleryItems tenantId={tenantId} galleryId={gallery.galleryId} />
    </Stack>
  );
}
